/*
  Отбор в реестре: семь условий и, на телефоне, кнопка, под которой они лежат.

  Вынесено из `pages/RegistryPage.jsx` 09.09.2026 вместе со сворачиванием.
  Причина сворачивания - замер: на экране в 390 пикселей семь полей идут
  столбиком и занимают около семисот пикселей, то есть весь первый экран и ещё
  немного. Человек, открывший реестр с телефона, видит семь раз «все» и ни
  одного счёта, пока не прокрутит.

  🔴 Ширину меряет ОФОРМЛЕНИЕ, а не код (раздел «Телефон и планшет» карты).
  Разметка на всех ширинах одна: кнопка стоит всегда, а видна только на узком
  экране; признак `открыто` на широком не значит ничего, потому что там правило
  показывает поля независимо от него. Спрашивать `innerWidth` здесь нельзя -
  тогда на вопрос «узкий ли экран» появилось бы два ответа, и они разошлись бы
  при первой правке порога.
*/

import { useEffect, useRef, useState } from 'react'

import { Field, Select } from './Common.jsx'

/*
  Порядок тот же, что в §3.3.6: сначала рабочая зона, потом подробности.

  «Работа» и «Сгоревшая часть» добавлены 29.09.2026 вместе со ссылками с плашек
  дашборда: без них ссылка задавала бы условие, которого в панели нет, и человек
  видел бы отобранный реестр, не понимая, чем он отобран. «Работа» стоит рядом со
  зоной и стадией - это про то же, про положение счёта; «Сгоревшая часть» - сразу
  за давностью, потому что это её продолжение.
*/
const FILTERS = [
  { name: 'zone', label: 'Рабочая зона', options: 'ZONE' },
  { name: 'stage', label: 'Стадия', options: 'STAGE' },
  { name: 'work', label: 'Работа', options: 'WORK' },
  { name: 'limitation', label: 'Давность', options: 'LIMITATION' },
  { name: 'expired', label: 'Сгоревшая часть', options: 'EXPIRED' },
  { name: 'amount', label: 'Сумма', options: 'AMOUNT' },
  { name: 'contactability', label: 'Связь', options: 'CONTACT' },
  { name: 'route', label: 'Куда идёт', options: 'ROUTE' },
]

/*
  Поиск строкой - первым в отборе (указание владельца 14.09.2026). «Должник»
  добавлен 19.10.2026 вместе с колонкой (решение владельца, макет
  «3-реестр-должник-А»): ищет по фамилии, имени или отчеству по всему портфелю.
*/
const SEARCHES = [
  { name: 'search', label: 'Лицевой счёт', placeholder: 'номер счёта' },
  { name: 'address', label: 'Адрес', placeholder: 'улица или дом' },
  { name: 'debtor', label: 'Должник', placeholder: 'фамилия или ФИО' },
]

/*
  Порядок строк списком - для узкого экрана (14.09.2026). На телефоне таблица
  разворачивается в карточки, заголовков колонок не видно вовсе, и нажимать там
  не на что: без этого поля сортировка существовала бы только на широком экране.

  Пара «поле и направление» одним значением: два списка рядом («по чему» и
  «как») заставляли бы делать два выбора там, где человек думает одной фразой.
*/
const ORDERS = {
  'amount:desc': 'сначала крупные долги',
  'amount:asc': 'сначала мелкие долги',
  'limitation:asc': 'сначала то, что сгорит раньше',
  'number:asc': 'по номеру лицевого счёта',
}

const BY_IMPORTANCE = 'по важности'

export default function RegistryFilters({ params, onChange, onOrder, titles }) {
  const [open, setOpen] = useState(false)

  // Счётчик применённых условий: под свёрнутой кнопкой не видно, отобрано
  // что-нибудь или показан весь портфель.
  const applied = [...FILTERS, ...SEARCHES].filter((filter) => params.get(filter.name)).length

  return (
    <>
      <button
        type="button"
        className="filters-toggle button button--quiet"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? 'Скрыть отбор' : 'Отбор'}
        {applied ? ` · ${applied}` : ''}
      </button>

      <div className={`filters${open ? ' filters--open' : ''}`}>
        {/*
          🔴 Поиск стоит ПЕРВЫМ (указание владельца 14.09.2026). До этого он был
          последним, под шестью выпадающими списками, - а это самое частое
          действие в реестре: человек ищет знакомый счёт или дом, а не перебирает
          оси триажа. Рядом с ним отбор по адресу: дом - то, чем мыслит юрист.
        */}
        {SEARCHES.map((search) => (
          <SearchField key={search.name} {...search} params={params} onChange={onChange} />
        ))}
        {FILTERS.map((filter) => (
          <Field key={filter.name} label={filter.label}>
            <Select
              value={params.get(filter.name)}
              onChange={(value) => onChange(filter.name, value)}
              options={titles[filter.options]}
            />
          </Field>
        ))}
        {/* Поле видно только на узком экране (`field--order` в оформлении):
            на широком тем же управляют заголовки колонок, и вторая точка входа
            в один и тот же порядок сбивала бы с толку. */}
        <Field label="Порядок" className="field--order">
          <Select
            value={_order(params)}
            onChange={onOrder}
            options={ORDERS}
            empty={BY_IMPORTANCE}
          />
        </Field>
      </div>
    </>
  )
}

/*
  Поле поиска: уходит на сервер по Enter и при уходе из поля, а не на каждую
  букву - каждый запрос разбирает весь портфель.
*/
function SearchField({ name, label, placeholder, params, onChange }) {
  const current = params.get(name) ?? ''
  const input = useRef(null)
  /*
    28.10.2026 (аудит 5.3): поле неуправляемое, и после «Назад» в нём оставался
    прежний текст, который при уходе из поля применялся снова. Теперь, когда
    отбор в адресе изменился не отсюда, текст поля подтягивается к нему (без
    пересоздания поля: после Enter фокус остаётся). То же значение заново не
    применяется - иначе Enter и следующий за ним уход из поля писали бы две
    записи в историю.
  */
  useEffect(() => {
    if (input.current && input.current.value !== current) input.current.value = current
  }, [current])
  const apply = (value) => {
    const text = value.trim()
    if (text !== current) onChange(name, text)
  }
  return (
    <Field label={label}>
      <input
        ref={input}
        className="field__input"
        type="search"
        placeholder={placeholder}
        defaultValue={current}
        onBlur={(event) => apply(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') apply(event.target.value)
        }}
      />
    </Field>
  )
}

/** Текущий порядок одной строкой - тем же ключом, каким названы варианты. */
function _order(params) {
  const sort = params.get('sort')
  if (!sort || sort === 'priority') return ''
  return `${sort}:${params.get('direction') ?? 'asc'}`
}
